import type { PageNumber } from "@pdfking/renderer-api"
import { Identity, SortBy } from "@pdfking/tfunc"

import type { Numbering, PageEntry, SortFunction } from "#internal"
import type { NumberedPage } from "#internal"

import { PathKey } from "../../common/layout/PathKey"
import { BluelinePageNumbers } from "./info/BluelinePageNumbers"
import { PageNumbersToSheetCoverIndex } from "./info/PageNumbersToSheetCoverIndex"
import { PageNumbersToSheetIndex } from "./info/PageNumbersToSheetIndex"
import { PageNumbersToSignatureIndex } from "./info/PageNumbersToSignatureIndex"
import { SheetCoverPageNumbers } from "./info/SheetCoverPageNumbers"
import { SignatureSizeForBinding } from "./info/SignatureSizeForBinding"

const is_printed = ({ is_front, meta }: PageEntry): boolean =>
  is_front ? meta.print_front : meta.print_rear

/**
 * Every page of the job numbered once, from 1: the same path is the same
 * page however many sheets it lands on, the pages read in their `sequence`,
 * the signatures reordered the way the job asks (`sort_function`), and only
 * the printed sides counted.
 */
export const NumberPages = (
  entries: Iterable<PageEntry>,
  sort_function: SortFunction = Identity
): Numbering => {
  const unique = new Map<string, PageEntry>()
  for (const entry of entries) {
    const key = PathKey(entry.path)
    if (!unique.has(key)) unique.set(key, entry)
  }
  const read = SortBy([...unique.values()], entry => [...entry.sequence])
  const printed: NumberedPage[] = [...sort_function(read)]
    .filter(is_printed)
    .map((entry, i) => ({ ...entry, page_number: (i + 1) as PageNumber }))
  const numbers = new Map(printed.map(page => [PathKey(page.path), page.page_number]))

  const blueline_page_numbers = BluelinePageNumbers(printed)
  const sheet_cover_page_numbers = SheetCoverPageNumbers(blueline_page_numbers)
  const page_numbers_to_sheet_cover_index = PageNumbersToSheetCoverIndex(sheet_cover_page_numbers)

  return {
    page_number_of: path => numbers.get(PathKey(path)),
    informations: {
      blueline_page_numbers,
      sheet_cover_page_numbers,
      page_numbers_to_sheet_index: PageNumbersToSheetIndex(blueline_page_numbers),
      page_numbers_to_sheet_cover_index,
      page_numbers_to_signature_index: PageNumbersToSignatureIndex(
        blueline_page_numbers,
        page_numbers_to_sheet_cover_index
      ),
      signature_size_for_binding: SignatureSizeForBinding(
        blueline_page_numbers,
        page_numbers_to_sheet_cover_index
      ),
    },
    printed,
  }
}
